import { Controller } from "@hotwired/stimulus"
import AirDatepicker from "air-datepicker"
import localeEn from "air-datepicker/locale/en"

// A themed calendar for date fields.
//
// The native <input type="date"> picker is drawn by the OS outside the page,
// so no stylesheet reaches it: the field could be made to match the app and
// the calendar it opened could not.
//
// Air Datepicker rather than flatpickr, after flatpickr proved to be the
// wrong shape for the job. Two reasons, and the second is the real one:
//
//   Its header is built for a static title. Getting a month CONTROL into it
//   meant fighting a hardcoded 307.875px grid, absolutely-positioned arrows
//   with no containing block, display: none on a disabled arrow, and
//   overflow: hidden clipping anything that hung down. Five passes and the
//   header still sat off-centre.
//
//   It has no year or month VIEW. The month could be made a dropdown, the
//   year could only ever be a text input, and reaching 1985 for a date of
//   birth meant typing or stepping. Air Datepicker has both natively: click
//   the title for a month grid, again for a year grid, pick, and you are
//   back on the days.
//
// It is also themed through CSS custom properties — --adp-width,
// --adp-cell-background-color-selected and the rest — so the skin in
// application.css sets variables instead of overriding hardcoded pixels.
export default class extends Controller {
  static values = {
    // A date of birth opens on the YEAR view: it is decades away, and the
    // day grid for August 2026 is not a useful place to start looking for
    // 1985. Everything else is a recent measurement, which opens on days
    // because the answer is nearly always this week.
    picker: { type: String, default: "recent" }
  }

  connect() {
    const birthday = this.pickerValue === "birthday"

    // The real input keeps its ISO value for the server; the visible one
    // shows "29 Aug 2026". A date field that displays 2026-08-29 is a
    // database talking.
    this.visible = document.createElement("input")
    this.visible.type = "text"
    this.visible.className = `${this.element.className} date-trigger`
    this.visible.readOnly = true
    this.ignoreFill(this.visible)
    this.element.after(this.visible)
    this.element.classList.add("sr-only")
    this.element.tabIndex = -1

    this.picker = new AirDatepicker(this.visible, {
      locale: localeEn,
      // The value the SERVER sees, written to the real input.
      altField: this.element,
      altFieldDateFormat: "yyyy-MM-dd",
      dateFormat: "d MMM yyyy",
      view: birthday ? "years" : "days",
      // Bounds inherited from the markup, so they stay declared beside the
      // field rather than restated here.
      maxDate: this.element.getAttribute("max") || undefined,
      minDate: this.element.getAttribute("min") || undefined,
      autoClose: true,
      selectedDates: this.element.value ? [this.element.value] : [],
      classes: "thrive-calendar",
      // ALIGNED TO THE FIELD'S RIGHT EDGE, which is what day_picker_controller
      // already does — its comment says why in as many words: "so a picker at
      // the strip's end does not hang off the page." These fields sit in a
      // flex row and the date one is near the right, so a 17.5rem panel
      // opening from its left ran off the screen. Cody, 14 Sep, and again
      // when isMobile was tried instead: "the datepicker on the today page is
      // fine because its anchored right - can't we do that on mobile too?"
      //
      // isMobile WAS THE WRONG ANSWER. It centres the panel and dims the
      // page — and dimmed the panel with it, because the library's overlay
      // sits above a picker this app gives its own z-index. A fix already in
      // the codebase beats one that brings its own bug.
      position: "bottom right",

      // A readonly text input does not open a keyboard on mobile and cannot
      // be typed into, so the calendar is the only way in — which is what we
      // want, since a free-text date is a parsing problem.
      isMobile: false
    })

    // Air Datepicker writes altField directly, which does not fire the
    // events a form or another controller would listen for.
    this.onSelect = () => {
      this.element.dispatchEvent(new Event("change", { bubbles: true }))
    }
    this.visible.addEventListener("change", this.onSelect)
  }

  // Password managers fill anything that looks like a form field, and a bare
  // text input beside a form is a magnet: 1Password drew its offer inside
  // the field, over the date. Four attributes because no single one is
  // respected everywhere.
  ignoreFill(input) {
    input.setAttribute("data-1p-ignore", "")
    input.setAttribute("data-lpignore", "true")
    input.setAttribute("data-bwignore", "")
    input.setAttribute("autocomplete", "off")
  }

  disconnect() {
    // Turbo caches pages and the calendar is appended to <body>, so without
    // this the panel outlives the page that opened it.
    this.visible?.removeEventListener("change", this.onSelect)
    this.picker?.destroy()
    this.picker = null
    this.visible?.remove()
    this.element.classList.remove("sr-only")
  }
}
